import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  Flex,
  Heading,
  LoadingOrbit,
  Text,
  TextField,
  useToast,
} from '@chatsdet/ui';
import { Plus, SquaresFour } from '@phosphor-icons/react';
import { useCreateDashboard, useDashboardsList } from './useDashboardData';

type Props = {
  onOpen: (id: string) => void;
};

export function DashboardsList({ onOpen }: Props) {
  const { data, isLoading } = useDashboardsList();
  const createMutation = useCreateDashboard();
  const { toast } = useToast();
  const [title, setTitle] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);

  const submit = () => {
    if (!title.trim()) return;
    createMutation.mutate(
      { title: title.trim() },
      {
        onSuccess: (dashboard) => {
          setTitle('');
          setIsOpen(false);
          onOpen(dashboard.id);
        },
        onError: (err) =>
          toast.error(`Failed to create dashboard: ${String(err)}`),
      }
    );
  };

  return (
    <Box className="p-6 space-y-4 overflow-auto h-full">
      <Flex align="center" justify="between">
        <Heading level={2}>Dashboards</Heading>
        <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen} size="md">
          <Button onPress={() => setIsOpen(true)}>
            <Plus size={14} /> New dashboard
          </Button>
          <Dialog title="New dashboard">
            <DialogContent>
              <TextField
                label="Title"
                value={title}
                onChange={setTitle}
                autoFocus
                placeholder="e.g. Activation funnel"
              />
            </DialogContent>
            <DialogFooter>
              <Button variant="outline" onPress={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button
                onPress={submit}
                isDisabled={!title.trim() || createMutation.isPending}
              >
                {createMutation.isPending ? 'Creating…' : 'Create'}
              </Button>
            </DialogFooter>
          </Dialog>
        </DialogTrigger>
      </Flex>

      {isLoading ? (
        <Flex align="center" justify="center" className="h-64">
          <LoadingOrbit />
        </Flex>
      ) : !data || data.length === 0 ? (
        <Flex
          direction="column"
          align="center"
          justify="center"
          className="h-64 text-center"
        >
          <SquaresFour size={48} className="text-muted-foreground/40 mb-3" />
          <Heading level={3} className="mb-1">
            No dashboards yet
          </Heading>
          <Text className="text-muted-foreground max-w-md">
            Create one to start arranging saved query cards on a grid.
          </Text>
        </Flex>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.map((d) => (
            <Card
              key={d.id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => onOpen(d.id)}
            >
              <CardContent className="p-4">
                <Heading level={4} className="truncate mb-1">
                  {d.title}
                </Heading>
                <Text size="sm" className="text-muted-foreground">
                  {d.cardCount} card{d.cardCount === 1 ? '' : 's'} · updated{' '}
                  {new Date(d.updatedAt).toLocaleDateString()}
                </Text>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </Box>
  );
}
